import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/utils';

type TopProduct = {
  lineCode: string;
  partNumber: string;
  description: string;
  unites: number;
  total: string;
};

type Dashboard = {
  event: { id: number; name: string; isActive: boolean } | null;
  totals: { orders: number; revenue: string; customers: number; visitors: number };
  byLine: { lineCode: string; total: string; unites: number }[];
  bySupplier: { key: string; name: string; total: string; unites: number }[];
  byRep: { fullName: string; username: string; commandes: number; total: string }[];
  topProducts: TopProduct[];
  topProductsByUnits: TopProduct[];
  hourly: { heure: string; commandes: number; total: string }[];
  generatedAt: string;
};

/**
 * Dashboard temps réel — conçu pour être projeté sur écran géant (plan §5.7).
 * Auto-refresh 30 s ; grands chiffres, lisibles de loin.
 */
export default function Dashboard() {
  const { data, isPending, isError, dataUpdatedAt } = useQuery<Dashboard>({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api('/api/admin/dashboard'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  // Classement du Top 10 : par valeur ($) ou par nombre d'unités.
  const [topBy, setTopBy] = useState<'value' | 'units'>('value');
  // Ventilation des ventes : par ligne (code) ou par fournisseur (groupe de lignes).
  const [ventesBy, setVentesBy] = useState<'ligne' | 'fournisseur'>('ligne');
  // Affichage des représentants : nom complet ou nom d'utilisateur.
  const [repBy, setRepBy] = useState<'nom' | 'utilisateur'>('nom');

  if (isPending) return <p className="text-muted-foreground p-8 text-center">Chargement…</p>;
  if (isError || !data.event) {
    return (
      <p className="text-muted-foreground p-8 text-center">
        Aucun événement à afficher. Activez un événement pour voir les ventes.
      </p>
    );
  }

  const maxHeure = Math.max(1, ...data.hourly.map((h) => Number(h.total)));

  // Le show s'étale sur plusieurs jours : on regroupe les barres horaires PAR
  // JOUR (la clé « heure » est « AAAA-MM-JJ HH:00 »), sinon 09:00 du jour 1 et du
  // jour 2 se confondent. L'échelle (maxHeure) reste commune pour comparer les jours.
  const parJour = new Map<string, Dashboard['hourly']>();
  for (const h of data.hourly) {
    const jour = h.heure.slice(0, 10);
    const arr = parJour.get(jour) ?? [];
    arr.push(h);
    parJour.set(jour, arr);
  }
  const jours = [...parJour.entries()];
  const formatJour = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y!, m! - 1, d!).toLocaleDateString('fr-CA', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-bold">{data.event.name}</h2>
        <p className="text-muted-foreground text-xs">
          À jour · {new Date(dataUpdatedAt).toLocaleTimeString('fr-CA')} · rafraîchit toutes les 30 s
        </p>
      </div>

      {/* Grands chiffres */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tuile label="Ventes du show" valeur={formatMoney(data.totals.revenue)} accent />
        <Tuile label="Commandes" valeur={String(data.totals.orders)} />
        <Tuile label="Clients servis" valeur={String(data.totals.customers)} />
        <Tuile label="Visiteurs (étiquettes)" valeur={String(data.totals.visitors)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Carte
          titre="Ventes"
          action={
            <div className="bg-secondary inline-flex rounded-lg p-0.5 text-sm">
              <button
                type="button"
                onClick={() => setVentesBy('ligne')}
                className={`rounded-md px-3 py-1 font-medium ${
                  ventesBy === 'ligne' ? 'bg-card shadow' : 'text-muted-foreground'
                }`}
              >
                Par ligne
              </button>
              <button
                type="button"
                onClick={() => setVentesBy('fournisseur')}
                className={`rounded-md px-3 py-1 font-medium ${
                  ventesBy === 'fournisseur' ? 'bg-card shadow' : 'text-muted-foreground'
                }`}
              >
                Par fournisseur
              </button>
            </div>
          }
        >
          {ventesBy === 'ligne' ? (
            data.byLine.length === 0 ? (
              <Vide />
            ) : (
              <ul className="space-y-2">
                {data.byLine.map((l) => (
                  <li key={l.lineCode} className="flex items-center justify-between">
                    <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-sm font-medium">
                      {l.lineCode}
                    </span>
                    <span className="text-muted-foreground ml-2 flex-1 text-sm">
                      {l.unites} unités
                    </span>
                    <span className="font-semibold tabular-nums">{formatMoney(l.total)}</span>
                  </li>
                ))}
              </ul>
            )
          ) : data.bySupplier.length === 0 ? (
            <Vide />
          ) : (
            <ul className="space-y-2">
              {data.bySupplier.map((f) => (
                <li
                  key={f.key}
                  className="grid grid-cols-[minmax(0,1fr)_5rem_7rem] items-center gap-2"
                >
                  <span className="min-w-0 truncate">{f.name}</span>
                  <span className="text-muted-foreground text-right text-sm tabular-nums">
                    {f.unites} u.
                  </span>
                  <span className="text-right font-semibold tabular-nums">
                    {formatMoney(f.total)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        <Carte
          titre="Par représentant"
          action={
            <div className="bg-secondary inline-flex rounded-lg p-0.5 text-sm">
              <button
                type="button"
                onClick={() => setRepBy('nom')}
                className={`rounded-md px-3 py-1 font-medium ${
                  repBy === 'nom' ? 'bg-card shadow' : 'text-muted-foreground'
                }`}
              >
                Nom
              </button>
              <button
                type="button"
                onClick={() => setRepBy('utilisateur')}
                className={`rounded-md px-3 py-1 font-medium ${
                  repBy === 'utilisateur' ? 'bg-card shadow' : 'text-muted-foreground'
                }`}
              >
                Utilisateur
              </button>
            </div>
          }
        >
          {data.byRep.length === 0 ? (
            <Vide />
          ) : (
            <ul className="space-y-2">
              {data.byRep.map((r) => {
                const libelle = repBy === 'utilisateur' ? r.username : r.fullName;
                return (
                  <li
                    key={r.username}
                    // Colonnes fixes (identité · nb commandes · total) pour un alignement net.
                    className="grid grid-cols-[minmax(0,1fr)_3.5rem_7rem] items-center gap-2"
                  >
                    <span className="min-w-0 truncate" title={libelle}>
                      {libelle}
                    </span>
                    <span className="text-muted-foreground text-right text-sm tabular-nums">
                      {r.commandes}
                    </span>
                    <span className="text-right font-semibold tabular-nums">
                      {formatMoney(r.total)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </Carte>
      </div>

      <Carte titre="Ventes par heure">
        {data.hourly.length === 0 ? (
          <Vide />
        ) : (
          <div className="space-y-6">
            {jours.map(([jour, heures]) => (
              <div key={jour}>
                <p className="text-muted-foreground mb-2 text-sm font-medium capitalize">
                  {formatJour(jour)}
                </p>
                <div className="flex items-end gap-2 overflow-x-auto" style={{ height: 160 }}>
                  {heures.map((h) => (
                    <div key={h.heure} className="flex min-w-10 flex-1 flex-col items-center gap-1">
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {formatMoney(h.total)}
                      </span>
                      <div
                        className="bg-primary w-full rounded-t"
                        style={{ height: `${Math.max(4, (Number(h.total) / maxHeure) * 120)}px` }}
                        title={`${h.heure} · ${h.commandes} commande(s)`}
                      />
                      <span className="text-muted-foreground text-xs">{h.heure.slice(11, 16)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Carte>

      <Carte
        titre="Top 10 produits"
        action={
          <div className="bg-secondary inline-flex rounded-lg p-0.5 text-sm">
            <button
              type="button"
              onClick={() => setTopBy('value')}
              className={`rounded-md px-3 py-1 font-medium ${
                topBy === 'value' ? 'bg-card shadow' : 'text-muted-foreground'
              }`}
            >
              Par valeur ($)
            </button>
            <button
              type="button"
              onClick={() => setTopBy('units')}
              className={`rounded-md px-3 py-1 font-medium ${
                topBy === 'units' ? 'bg-card shadow' : 'text-muted-foreground'
              }`}
            >
              Par unités
            </button>
          </div>
        }
      >
        {(topBy === 'units' ? data.topProductsByUnits : data.topProducts).length === 0 ? (
          <Vide />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[36rem] table-fixed text-sm">
              <thead>
                <tr className="text-muted-foreground text-left">
                  <th className="w-40 pb-2 font-medium">Pièce</th>
                  <th className="pb-2 font-medium">Description</th>
                  <th
                    className={`w-24 pb-2 pl-4 text-right font-medium ${
                      topBy === 'units' ? 'text-foreground' : ''
                    }`}
                  >
                    Unités
                  </th>
                  <th
                    className={`w-36 pb-2 pl-6 text-right font-medium ${
                      topBy === 'value' ? 'text-foreground' : ''
                    }`}
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {(topBy === 'units' ? data.topProductsByUnits : data.topProducts).map((p) => (
                  <tr key={`${p.lineCode}-${p.partNumber}`} className="border-t">
                    <td className="py-2 align-top font-mono text-xs">
                      <span className="text-muted-foreground">{p.lineCode}</span> {p.partNumber}
                    </td>
                    <td className="py-2">
                      {/* Une seule ligne : au-delà, tronqué avec « … » (texte complet au survol). */}
                      <div className="truncate" title={p.description}>
                        {p.description}
                      </div>
                    </td>
                    <td
                      className={`py-2 pl-4 text-right tabular-nums whitespace-nowrap ${
                        topBy === 'units' ? 'text-foreground font-semibold' : ''
                      }`}
                    >
                      {p.unites}
                    </td>
                    <td
                      className={`py-2 pl-6 text-right tabular-nums whitespace-nowrap ${
                        topBy === 'value' ? 'font-semibold' : 'font-medium'
                      }`}
                    >
                      {formatMoney(p.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>
    </div>
  );
}

function Tuile({ label, valeur, accent }: { label: string; valeur: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-6 ${accent ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>
      <p className={accent ? 'text-primary-foreground/80 text-sm' : 'text-muted-foreground text-sm'}>
        {label}
      </p>
      <p className="mt-1 text-4xl font-bold tabular-nums">{valeur}</p>
    </div>
  );
}

function Carte({
  titre,
  action,
  children,
}: {
  titre: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-card rounded-xl border p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">{titre}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Vide() {
  return <p className="text-muted-foreground text-sm">Aucune vente pour l'instant.</p>;
}
